import React, { useState, useMemo, useEffect } from 'react';
import { Input, Checkbox, Button, Select, Spin } from 'antd';
import { SearchOutlined, LockOutlined, ArrowLeftOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router';
import axios from 'axios';
const TransferDashboardsScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan') || 'Starter';
  const credits = searchParams.get('credits') || '500';
  const billingCycle = searchParams.get('billingCycle') || 'Annual';
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const {
    VITE_APP_URL: APP_URL
  } = import.meta.env;
  const userId = localStorage.getItem("userId");
  const [dashboards, setDashboards] = useState<{
    id: string;
    name: string;
  }[]>([]);
  const [loading, setLoading] = useState(false);
  const MAX_TRANSFER = 5;
  useEffect(() => {
    const fetchDashboards = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${APP_URL}/api/dashboards/dashboards_filehandlingss/${userId}`);

        const extractDashboards = (obj: any, acc: any[] = []): any[] => {
          if (!obj) return acc;
          const target = obj?.tree || obj;
          if (Array.isArray(target)) {
            target.forEach((item: any) => {
              if (item && typeof item === 'object' && item.id != null && item.name) {
                acc.push(item);
              }
            });
          } else if (typeof target === 'object') {
            Object.entries(target).forEach(([key, val]) => {
              if (key !== "dashboard_ids" && key !== "shared_dashboard_ids") {
                extractDashboards(val, acc);
              }
            });
          }
          return acc;
        };
        const list = extractDashboards(response.data);
        setDashboards(list.map(item => ({
          id: String(item.id),
          name: item.name
        })));
      } catch (error) {
        console.error("Error fetching dashboards:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboards();
  }, []);
  const filteredData = useMemo(() => {
    return dashboards.filter(item => item.name.toLowerCase().includes(searchText.toLowerCase()));
  }, [searchText, dashboards]);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);
  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      const idsToAdd = paginatedData.map(d => d.id).filter(id => !selectedIds.includes(id));
      let newSelected = [...selectedIds];
      for (const id of idsToAdd) {
        if (newSelected.length < MAX_TRANSFER) {
          newSelected.push(id);
        }
      }
      setSelectedIds(newSelected);
    } else {
      setSelectedIds(selectedIds.filter(id => !paginatedData.map(d => d.id).includes(id)));
    }
  };
  const handleSelectRow = (id: string, checked: boolean) => {
    if (checked) {
      if (selectedIds.length < MAX_TRANSFER) {
        setSelectedIds([...selectedIds, id]);
      }
    } else {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    }
  };
  const isAllSelected = paginatedData.length > 0 && paginatedData.every(d => selectedIds.includes(d.id));
  const isIndeterminate = paginatedData.some(d => selectedIds.includes(d.id)) && !isAllSelected;
  return <div className="w-full h-full p-8 flex flex-col bg-[#F8FAFC]">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-3">Transfer Your Dashboards</h1>
          <p className="text-gray-500 text-base">
            Select the dashboards you want to transfer to your {plan.charAt(0).toUpperCase() + plan.slice(1)} plan.<br />
            You can transfer up to <span className="text-[#C41230] font-bold">{MAX_TRANSFER} dashboards</span>.
          </p>
        </div>
        <div className="flex items-center gap-3 px-5 py-3 bg-[#FFF5F6] border border-[#FFE1E6] text-[#C41230] rounded-lg font-medium shadow-sm">
          <LockOutlined className="text-xl" />
          <span>{MAX_TRANSFER} / {MAX_TRANSFER} dashboards can be transferred</span>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col flex-1">
        {/* Toolbar */}
        <div className="flex justify-between items-center mb-6">
          <Input prefix={<SearchOutlined className="text-gray-400" />} placeholder="Search dashboards..."
        // className="w-[200px]"
        style={{
          width: "400px"
        }} size="large" value={searchText} onChange={e => {
          setSearchText(e.target.value);
          setCurrentPage(1);
        }} />
          
          <div className="flex items-center gap-6">
            <span className="text-gray-500">{selectedIds.length} selected</span>
            <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={handleSelectAll} disabled={selectedIds.length >= MAX_TRANSFER && !isAllSelected}>
              
              <span className="font-semibold text-gray-800">Select All</span>
            </Checkbox>
          </div>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-[60px_1fr] gap-4 py-4 border-b border-gray-100 text-[#C41230] font-semibold text-sm px-2">
          <div className="flex justify-center">
             {/* Header checkbox area left empty as per design, Select All is above */}
          </div>
          <div>Dashboard Name</div>
        </div>

        {/* Data Rows */}
        <div className="flex flex-col flex-1 min-h-[350px]">
          {loading ? <div className="py-12 flex items-center justify-center">
              <Spin size="large" />
            </div> : <>
              {paginatedData.map(dashboard => {
            const isSelected = selectedIds.includes(dashboard.id);
            const isDisabled = !isSelected && selectedIds.length >= MAX_TRANSFER;
            return <button key={dashboard.id} onClick={() => {
              if (!isDisabled) {
                handleSelectRow(dashboard.id, !isSelected);
              }
            }} className={`grid grid-cols-[60px_1fr] gap-4 py-5 border-b border-gray-50 items-center transition-colors px-2 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`} tabIndex={0} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}>
                  
                    <div className="flex justify-center pointer-events-none">
                      <Checkbox checked={isSelected} disabled={isDisabled} />
                    
                    </div>
                    <div className="font-semibold text-gray-700 select-none">{dashboard.name}</div>
                  </button>;
          })}
              
              {paginatedData.length === 0 && <div className="py-12 flex items-center justify-center text-gray-400">
                  No dashboards found
                </div>}
            </>}
        </div>

        {/* Footer / Pagination */}
        <div className="flex justify-between items-center mt-6 pt-2">
          <div className="text-gray-500 font-medium text-sm">
            Showing {filteredData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} dashboards
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button size="middle" className="text-gray-400 flex items-center justify-center border-gray-200" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>
                
                {'<'}
              </Button>
              <div className="w-8 h-8 flex items-center justify-center bg-[#FFF5F6] text-[#C41230] font-bold rounded border border-[#FFE1E6]">
                {currentPage}
              </div>
              <Button size="middle" className="text-gray-400 flex items-center justify-center border-gray-200" disabled={currentPage * pageSize >= filteredData.length} onClick={() => setCurrentPage(prev => prev + 1)}>
                
                {'>'}
              </Button>
            </div>
            <Select value={pageSize} onChange={val => {
            setPageSize(val);
            setCurrentPage(1);
          }} options={[{
            value: 5,
            label: '5 per page'
          }, {
            value: 10,
            label: '10 per page'
          }, {
            value: 20,
            label: '20 per page'
          }]} className="w-32" />
            
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="flex justify-center gap-6 mt-8">
        <Button icon={<ArrowLeftOutlined />} size="large" className="w-48 font-semibold" style={{
        borderColor: '#C41230',
        color: '#C41230'
      }} onClick={() => navigate(-1)}>
          
          Back
        </Button>
        <Button type="primary" icon={<CloudUploadOutlined />} size="large" className="w-64 font-semibold" style={{
        backgroundColor: selectedIds.length === 0 ? undefined : '#C41230'
      }} disabled={selectedIds.length === 0} onClick={() => {
        navigate(`/dashboard/transfer-dashboards-success?plan=${plan}&count=${selectedIds.length}&credits=${credits}&billingCycle=${billingCycle}`);
      }}>
          
          Transfer Selected ({selectedIds.length})
        </Button>
      </div>
    </div>;
};
export default TransferDashboardsScreen;